import type {
    Asset,
    CbctProcessingApi,
    CbctProcessingStatusResult,
    CbctProcessingTriggerOptions
} from '@odo/shared-ui';
import { fetchCbctProcessingStatus, triggerCbctProcessing } from './patientFiles';

interface AxiosLikeError {
    response?: { status?: number; data?: { error?: { message?: string }; message?: string } };
    message?: string;
}

function extractErrorMessage(err: unknown): string {
    const e = err as AxiosLikeError;
    return (
        e?.response?.data?.error?.message ||
        e?.response?.data?.message ||
        e?.message ||
        'Failed to reach CBCT processing'
    );
}

function requirePatientFileId(asset: Asset): string {
    if (!asset.patientFileId) throw new Error('This file cannot be processed (no patientFileId).');
    return asset.patientFileId;
}

/** files-viewer impl of the shared cbctProcessing capability. */
export function createCbctProcessingApi(patientId: string): CbctProcessingApi {
    return {
        async getStatus(asset: Asset): Promise<CbctProcessingStatusResult> {
            const patientFileId = requirePatientFileId(asset);
            try {
                const job = await fetchCbctProcessingStatus(patientId, patientFileId);
                if (!job) return { status: 'NOT_STARTED' };
                return job.status === 'FAILED'
                    ? { status: 'FAILED', error: job.errorMessage ?? undefined }
                    : { status: job.status };
            } catch (err) {
                // A missing job/file (404) means processing was never started.
                if ((err as AxiosLikeError)?.response?.status === 404) {
                    return { status: 'NOT_STARTED' };
                }
                throw new Error(extractErrorMessage(err));
            }
        },
        async trigger(asset: Asset, options?: CbctProcessingTriggerOptions): Promise<void> {
            const patientFileId = requirePatientFileId(asset);
            try {
                await triggerCbctProcessing(patientId, patientFileId, options?.password);
            } catch (err) {
                throw new Error(extractErrorMessage(err));
            }
        }
    };
}
